import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';

/**
 * HammerHero — landing-page hero for absolutehammers.com.
 *
 * Uses a static cinematic composite (nano-banana generated) as the backdrop
 * with a subtle parallax + cue-swap headline. When the user provides a
 * scroll-controlled video, swap the <img> for a <video> and reuse the
 * scroll-progress pattern from ScrollVideoHero.jsx.
 */
export default function HammerHero() {
  const wrapRef = useRef(null);
  const [cueIndex, setCueIndex] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const rect = wrap.getBoundingClientRect();
      const total = Math.max(wrap.offsetHeight - window.innerHeight, 1);
      const scrolled = Math.min(Math.max(-rect.top, 0), total);
      const p = scrolled / total;
      setProgress(p);
      // 0-50% : "Pneumatic Power, Evolved." | 50-100% : "Drive deeper. Drive faster."
      const next = p < 0.5 ? 0 : 1;
      setCueIndex((prev) => (prev === next ? prev : next));
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <section
      ref={wrapRef}
      id="hammer-top"
      data-testid="hammer-hero"
      className="relative w-full"
      style={{ height: '180vh' }}
    >
      <div className="sticky top-0 h-screen w-full overflow-hidden bg-black">
        {/* Hero backdrop — nano-banana generated composite with graceful CSS fallback.
            Outer div receives scroll-progress transform; inner img runs a slow
            Ken Burns idle animation so the still feels alive. */}
        <div className="absolute inset-0">
          <div
            className="absolute inset-0"
            style={{
              transform: `scale(${1.02 + progress * 0.06}) translateY(${progress * -24}px)`,
              transition: 'transform 300ms linear',
              willChange: 'transform',
            }}
          >
            <img
              src="/assets/hammer_hero.jpg?v=17"
              alt=""
              aria-hidden="true"
              className="hammer-hero-kenburns absolute inset-0 w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </div>
          {/* CSS fallback / atmospheric enhancer, sits behind the image but shows through if img errors */}
          <div
            className="absolute inset-0 -z-10"
            style={{
              background:
                'radial-gradient(70% 55% at 62% 55%, rgba(45,212,212,0.35) 0%, rgba(45,212,212,0.06) 42%, rgba(0,0,0,0) 75%), #000',
            }}
          />
        </div>

        {/* Vignette */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(80% 70% at 55% 55%, rgba(0,0,0,0) 30%, rgba(0,0,0,0.55) 75%, rgba(0,0,0,0.9) 100%)',
          }}
        />
        <div className="absolute inset-0 pointer-events-none teal-fog opacity-45" />

        {/* Foreground content */}
        <div className="relative z-10 h-full max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 flex flex-col justify-between pt-[14vh] sm:pt-[16vh] pb-[10vh] sm:pb-[12vh] md:pb-[14vh]">
          <div>
            <div className="ghost-text-tight whitespace-nowrap text-[10vw] sm:text-[9vw] md:text-[8vw] lg:text-[7vw] xl:text-[6.5vw] 2xl:text-[125px] leading-[0.85] text-left">
              ABSOLUTE HAMMERS
            </div>
          </div>

          <div>
            <h1
              key={cueIndex}
              className="font-display text-white font-bold uppercase leading-[0.95] tracking-tight hero-title-fade text-[8vw] sm:text-[7vw] md:text-[5.5vw] lg:text-[4.8vw] max-w-5xl"
            >
              {cueIndex === 0
                ? 'Pneumatic Power, Evolved.'
                : 'Hits harder, and smarter.'}
            </h1>

            <div className="mt-6 sm:mt-8 flex flex-wrap items-center gap-3 sm:gap-4">
              <a
                href="#hammer-quote"
                data-testid="hammer-hero-buy-btn"
                className="btn-teal px-5 sm:px-6 py-3 rounded-md text-xs sm:text-sm uppercase tracking-[0.16em] sm:tracking-[0.18em] whitespace-nowrap"
              >
                Buy Now&nbsp;·&nbsp;$10,500*
              </a>
              <a
                href="#hammer-specs"
                data-testid="hammer-hero-specs-btn"
                className="btn-ghost px-5 sm:px-6 py-3 rounded-md text-xs sm:text-sm uppercase tracking-[0.16em] sm:tracking-[0.18em] whitespace-nowrap"
              >
                Features & Specs
              </a>
            </div>
          </div>
        </div>

        {/* Scroll cue */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 text-zinc-400 text-[10px] sm:text-[11px] uppercase tracking-[0.3em]">
          <span>Scroll</span>
          <ChevronDown className="w-4 h-4 animate-bounce" />
        </div>
      </div>
    </section>
  );
}
